import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, Globe2, Anchor } from "lucide-react";
import { smoothScrollTo } from "@/lib/SmoothScroll";

const HERO_BG =
  "https://images.pexels.com/photos/15674629/pexels-photo-15674629.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=1900";

/** Masked line-by-line reveal — each line is inside overflow-hidden and rises from y=100% */
const revealVariant = {
  hidden: { y: "110%" },
  show: (i = 0) => ({
    y: "0%",
    transition: {
      duration: 1.05,
      delay: 0.15 + i * 0.12,
      ease: [0.19, 1, 0.22, 1],
    },
  }),
};

const fadeUp = {
  hidden: { opacity: 0, y: 26 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.9,
      delay: 0.6 + i * 0.1,
      ease: [0.19, 1, 0.22, 1],
    },
  }),
};

const MaskLine = ({ children, i = 0, className = "" }) => (
  <span className={`block overflow-hidden ${className}`}>
    <motion.span
      variants={revealVariant}
      initial="hidden"
      animate="show"
      custom={i}
      className="block will-change-transform"
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.08, 1.22]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const veilOpacity = useTransform(scrollYProgress, [0, 1], [0.72, 0.92]);

  const go = (id) => {
    const el = document.getElementById(id);
    if (el) smoothScrollTo(el, { offset: -80 });
  };

  return (
    <section
      id="home"
      ref={ref}
      className="relative min-h-[100svh] w-full flex items-end text-white overflow-hidden"
      data-testid="hero-section"
    >
      {/* Parallax background */}
      <motion.div
        className="absolute inset-0 bg-cover bg-center will-change-transform"
        style={{
          backgroundImage: `url(${HERO_BG})`,
          y: bgY,
          scale: bgScale,
        }}
        aria-hidden="true"
      />
      {/* Multi-layer overlay for legibility + editorial mood */}
      <motion.div
        className="absolute inset-0 bg-[#060F1E]"
        style={{ opacity: veilOpacity }}
        aria-hidden="true"
      />
      <div
        className="absolute inset-0 bg-gradient-to-b from-[#060F1E]/50 via-transparent to-[#060F1E]"
        aria-hidden="true"
      />
      <div className="absolute inset-0 grain-overlay pointer-events-none" />

      {/* Editorial side chrome */}
      <div className="absolute inset-y-0 left-0 hidden md:flex flex-col justify-between py-24 pl-6 z-10 pointer-events-none">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.4, duration: 0.9 }}
          className="font-accent text-[10px] text-[#D4AF37]/80"
          style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}
        >
          Est. 2011 · Rahilaxmi Exports LLP
        </motion.div>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.6, duration: 0.9 }}
          className="font-accent text-[10px] text-white/40"
        >
          N 18.6298° · E 73.7997°
        </motion.div>
      </div>

      <div className="absolute inset-y-0 right-0 hidden md:flex flex-col justify-between py-24 pr-6 z-10 items-end pointer-events-none">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.4, duration: 0.9 }}
          className="flex items-center gap-2 text-white/50 text-[10px] font-accent"
        >
          <Globe2 size={14} className="text-[#D4AF37]" /> Worldwide Shipping
        </motion.div>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.6, duration: 0.9 }}
          className="flex items-center gap-2 text-white/50 text-[10px] font-accent"
        >
          <Anchor size={14} className="text-[#D4AF37]" /> JNPT · Mundra
        </motion.div>
      </div>

      {/* Main content */}
      <motion.div
        style={{ y: contentY }}
        className="relative z-10 max-w-[1440px] mx-auto w-full px-6 md:px-12 pb-16 md:pb-24 pt-40"
      >
        {/* Eyebrow */}
        <motion.div
          variants={fadeUp}
          initial="hidden"
          animate="show"
          custom={-2}
          className="flex items-center gap-4 mb-10"
          data-testid="hero-eyebrow"
        >
          <motion.span
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: 0.9, delay: 0.1, ease: [0.19, 1, 0.22, 1] }}
            className="h-px w-16 bg-[#D4AF37] origin-left"
          />
          <span className="font-accent text-[10px] text-[#D4AF37]">
            Rahilaxmi Exports LLP · Pune, India
          </span>
        </motion.div>

        {/* Masked line-by-line reveal */}
        <h1
          className="font-display font-bold leading-[0.92] tracking-tight max-w-[95%]"
          data-testid="hero-title"
          style={{ fontSize: "clamp(3rem, 8.6vw, 9rem)" }}
        >
          <MaskLine i={0}>Global Reach.</MaskLine>
          <MaskLine i={1}>
            <span className="italic font-normal text-[#D4AF37]" style={{ fontFamily: 'Fraunces, Georgia, serif' }}>
              Uncompromised
            </span>{" "}
            Quality.
          </MaskLine>
        </h1>

        <div className="mt-10 grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
          <motion.p
            variants={fadeUp}
            initial="hidden"
            animate="show"
            custom={0}
            className="md:col-span-6 text-lg md:text-xl text-white/75 leading-relaxed max-w-2xl"
            data-testid="hero-subtitle"
          >
            Exporting premium Haldi, Spices, Vegetables and Agricultural
            products to 25+ countries — sourced directly from Indian farms,
            processed to international standards.
          </motion.p>

          <motion.div
            variants={fadeUp}
            initial="hidden"
            animate="show"
            custom={1}
            className="md:col-span-6 flex flex-wrap items-center gap-4 md:justify-end"
          >
            <button
              onClick={() => go("inquiry")}
              className="group relative inline-flex items-center gap-3 px-8 py-4 bg-[#D4AF37] text-[#0A192F] font-semibold tracking-[0.16em] uppercase text-[11px] overflow-hidden"
              data-testid="hero-cta-quote"
            >
              <span className="absolute inset-0 bg-white translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]" />
              <span className="relative z-10">Request a Quote</span>
              <ArrowUpRight
                size={16}
                className="relative z-10 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
              />
            </button>
            <button
              onClick={() => go("products")}
              className="inline-flex items-center gap-3 px-8 py-4 border border-white/30 text-white font-semibold tracking-[0.16em] uppercase text-[11px] hover:border-[#D4AF37] hover:text-[#D4AF37] transition-all duration-300"
              data-testid="hero-cta-products"
            >
              View Products
            </button>
          </motion.div>
        </div>

        {/* Bottom stats strip */}
        <div
          className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-10 border-t border-white/10 pt-8"
        >
          {[
            { k: "Years", v: "10+", sub: "of trade expertise" },
            { k: "Countries", v: "25+", sub: "served globally" },
            { k: "Products", v: "5+", sub: "categories" },
            { k: "Quality", v: "100%", sub: "lab-tested batches" },
          ].map((it, i) => (
            <motion.div
              key={it.k}
              variants={fadeUp}
              initial="hidden"
              animate="show"
              custom={2 + i * 0.6}
              className="pl-4 border-l border-white/15"
              data-testid={`hero-stat-${it.k.toLowerCase()}`}
            >
              <div
                className="font-display text-white font-bold"
                style={{ fontSize: "clamp(1.75rem, 3vw, 2.75rem)" }}
              >
                {it.v}
              </div>
              <div className="font-accent text-[10px] text-[#D4AF37] mt-2">
                {it.k}
              </div>
              <div className="text-xs text-white/55 mt-1">{it.sub}</div>
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* Scroll cue */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.7, duration: 0.9 }}
        className="absolute bottom-4 left-1/2 -translate-x-1/2 hidden md:flex flex-col items-center gap-2 text-white/50 z-10"
      >
        <span className="font-accent text-[9px] tracking-[0.28em]">Scroll</span>
        <motion.span
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
          className="w-px h-8 bg-gradient-to-b from-white/60 to-transparent"
        />
      </motion.div>
    </section>
  );
};

export default Hero;
